// Typed client for GET/POST/PATCH/DELETE /api/v1/measurements (ROADMAP.md v0.6.5). Replaces
// MapContext.tsx's old React-memory-only measurement store — geometry travels as GeoJSON in the
// map's own view projection (EPSG:3857); the API reprojects to EPSG:32638 for storage
// server-side (ARCHITECTURE.md Sec 5 -- never "nudged" client-side).

import { apiFetch } from './platformApi';

// No @types/geojson in this project (OL doesn't pull it in transitively) -- a minimal structural
// type is enough here, since this module only ever passes geometry through OL's own GeoJSON
// format reader/writer (map/measurement.ts), never inspects `coordinates` itself.
export interface GeoJsonGeometry {
  type: string;
  coordinates?: unknown;
  geometries?: unknown;
  [key: string]: unknown;
}

export type ApiMeasurementType = 'point' | 'line' | 'zone' | 'circle';

export interface ApiMeasurement {
  measurement_id: string;
  owner_username: string;
  name: string;
  description: string | null;
  measurement_type: ApiMeasurementType;
  color: string | null;
  visible: boolean;
  geometry: GeoJsonGeometry;
  created_at: string;
  updated_at: string;
  /** The workspace slug this shape belongs to -- it only ever shows in that workspace. */
  workspace: string;
  folder_id: string | null;
}

export interface MeasurementCreateBody {
  name: string;
  description?: string | null;
  measurement_type: ApiMeasurementType;
  color?: string | null;
  visible?: boolean;
  geometry: GeoJsonGeometry;
  workspace: string;
  folder_id?: string | null;
}

export interface MeasurementPatchBody {
  name?: string;
  description?: string | null;
  color?: string | null;
  visible?: boolean;
  geometry?: GeoJsonGeometry;
  /** Sent only to move it: a folder id files it, null takes it out of its folder. */
  folder_id?: string | null;
}

/** One workspace's measurements -- every shape belongs to exactly one workspace. */
export async function fetchMeasurements(workspace: string): Promise<ApiMeasurement[]> {
  const res = await apiFetch(`/api/v1/measurements?workspace=${encodeURIComponent(workspace)}`);
  if (!res.ok) throw new Error(`GET /api/v1/measurements -> ${res.status}`);
  const body: unknown = await res.json();
  if (!Array.isArray(body)) throw new Error('GET /api/v1/measurements: expected a JSON array');
  return body as ApiMeasurement[];
}

export async function createMeasurement(body: MeasurementCreateBody): Promise<ApiMeasurement> {
  const res = await apiFetch('/api/v1/measurements', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST /api/v1/measurements -> ${res.status}`);
  return (await res.json()) as ApiMeasurement;
}

export async function patchMeasurement(id: string, body: MeasurementPatchBody): Promise<ApiMeasurement> {
  const res = await apiFetch(`/api/v1/measurements/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`PATCH /api/v1/measurements/${id} -> ${res.status}`);
  return (await res.json()) as ApiMeasurement;
}

export async function deleteMeasurement(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/measurements/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`DELETE /api/v1/measurements/${id} -> ${res.status}`);
}
